// src/components/Footer.js

import React from 'react';
import { 
  Box, 
  Container, 
  Grid, 
  Typography, 
  Link, 
  IconButton, 
  Divider, 
  Tooltip 
} from '@mui/material';
import InstagramIcon from '@mui/icons-material/Instagram';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import CodeIcon from '@mui/icons-material/Code'; // Optional icon for developer

import Coraidi_LOGO from './assets/Logo/Vedha1.png'; // Importing Coraidi logo

// --- Color Palette ---
// Note: #B9935A is a Gold/Earth tone, kept variable name as requested though visually it is not Teal
const DARK_PEACOCK_TEAL = '#B9935A'; 
const STONE_WHITE = '#FFFFFF';
const LIGHT_TEMPLE_GOLD = '#F7F2E9';

const Footer = () => {
  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: DARK_PEACOCK_TEAL,
        color: STONE_WHITE,
        pt: { xs: 6, md: 8 },
        pb: 4,
        borderTop: `1px solid rgba(255, 255, 255, 0.1)`,
        width: '100vw', // Changed to 100% to prevent horizontal scrollbar issues common with 100vw
        overflowX: 'hidden'
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={{ xs: 4, md: 5 }} sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
          
          {/* Column 1: Logo & Mission */}
          <Grid item size={{ xs:12, sm: 6, md: 4 }}>
            <Typography
              variant="h5"
              component="div"
              gutterBottom
              sx={{
                fontFamily: '"Cormorant Garamond", "Georgia", serif',
                fontWeight: 700,
                color: LIGHT_TEMPLE_GOLD,
                letterSpacing: '1px',
              }}
            >
              Bella Ceylon Voyages
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', lineHeight: 1.8 }}>
              Crafting immersive, personal journeys through the heart of Sri Lanka, connecting travelers to the true spirit of the island.
            </Typography>
          </Grid>

          {/* Column 2: Quick Links */}
          <Grid item size={{ xs:12, sm: 6, md: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
              Quick Links
            </Typography>
            <Link href="/experiences" variant="body2" display="block" color="inherit" sx={linkStyles}>Experiences</Link>
            <Link href="/destinations" variant="body2" display="block" color="inherit" sx={linkStyles}>Destinations</Link>
            <Link href="/about" variant="body2" display="block" color="inherit" sx={linkStyles}>About Us</Link>
            <Link href="/contact" variant="body2" display="block" color="inherit" sx={linkStyles}>Contact</Link>
          </Grid>

          {/* Column 3: Contact Info (UPDATED) */}
          <Grid item size={{ xs:12, sm: 6, md: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
              Contact Us
            </Typography>
            
            {/* Email */}
            <Link href="mailto:bellaceylonvoyages@gmail.com" variant="body2" display="block" color="inherit" sx={linkStyles}>
              bellaceylonvoyages@gmail.com
            </Link>
            
            {/* Phone */}
            <Link href="tel:+94777272723" variant="body2" display="block" color="inherit" sx={linkStyles}>
              +94 77 727 2723
            </Link>

            {/* Address */}
            <Typography variant="body2" sx={{ mt: 2, color: 'rgba(255, 255, 255, 0.8)', lineHeight: 1.6, whiteSpace: 'pre-line' }}>
              18/3B, Casia Avenue,{'\n'}
              Mount Lavinia, Sri Lanka 🇱🇰{'\n'}
              PO Box 10370
            </Typography>
          </Grid>

          {/* Column 4: Social Media */}
          <Grid item size={{ xs:12, sm: 6, md: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
              Follow Us
            </Typography>
            <Box>
              <IconButton aria-label="Instagram" color="inherit" href="https://instagram.com" target="_blank" sx={iconButtonStyles}>
                <InstagramIcon />
              </IconButton>
              <IconButton aria-label="Facebook" color="inherit" href="https://facebook.com" target="_blank" sx={iconButtonStyles}>
                <FacebookIcon />
              </IconButton>
              <IconButton aria-label="Twitter" color="inherit" href="https://twitter.com" target="_blank" sx={iconButtonStyles}>
                <TwitterIcon />
              </IconButton>
            </Box>
          </Grid>
        </Grid>
        
        {/* Bottom Row: Copyright & Developer Credit */}
        <Divider sx={{ my: 4, borderColor: 'rgba(255, 255, 255, 0.15)' }} />
        
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' }, // Stack on mobile, row on desktop
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
          }}
        >
          {/* Left Side: Copyright */}
          

          {/* Right Side: Developer Credit (Coraidi) */}
          <Box sx={{ display: 'flex', alignItems: 'center', color: 'rgba(255, 255, 255, 0.6)' }}>
            <Typography variant="caption" sx={{ mr: 0.5 }}>
              Powered by
            </Typography>
            
            <Tooltip 
              title={
                <Box sx={{ textAlign: 'center', p: 1 }}>
                  {/* Logo Image */}
                  <Box 
                    component="img"
                    src={Coraidi_LOGO} 
                    alt="Coraidi Logo"
                    sx={{ height: 40, width: 'auto', mb: 1, objectFit: 'contain' }}
                  />
                  <Typography variant="caption" display="block" sx={{ fontWeight: 'bold' }}>
                    Coraidi (Pvt) Ltd
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ fontSize: '0.7rem' }}>
                    Click to visit website
                  </Typography>
                </Box>
              } 
              arrow 
              placement="top"
            >
              <Link 
                href="https://www.coraidi.com" 
                target="_blank" 
                rel="noopener noreferrer"
                sx={{ 
                    ...linkStyles, 
                    mb: 0, 
                    fontWeight: 700, 
                    color: LIGHT_TEMPLE_GOLD,
                    textDecoration: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5
                }}
              >
                Coraidi (Pvt) Ltd
              </Link>
            </Tooltip>
          </Box>
          <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
              © {new Date().getFullYear()} Bella Ceylon Voyages. All Rights Reserved.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: { xs: 'center', md: 'flex-start' }, mt: 0.5 }}>
               <Link href="/privacy-policy" variant="caption" color="inherit" sx={{...linkStyles, mb:0}}>
                Privacy Policy
              </Link>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

// --- Reusable Styles ---
const linkStyles = {
  textDecoration: 'none',
  mb: 1,
  color: 'rgba(255, 255, 255, 0.7)',
  transition: 'all 0.3s ease',
  '&:hover': {
    color: LIGHT_TEMPLE_GOLD,
    transform: 'translateX(2px)', // Subtle movement effect
    cursor: 'pointer'
  }
};

const iconButtonStyles = {
    transition: 'all 0.3s ease',
    '&:hover': {
        color: LIGHT_TEMPLE_GOLD,
        transform: 'translateY(-3px)' // Subtle lift effect
    }
}

export default Footer;